/**
 * Drawing colours. The light theme reproduces SPECIFICATION.md clause 12
 * exactly; the dark theme re-tunes grid and guide values rather than inverting
 * them (design document C9).
 */
export interface DrawTheme {
  readonly background: string;
  readonly gridAxis: string;
  readonly gridMajor: string;
  readonly gridMinor: string;
  readonly gridLabel: string;
  readonly gridCaption: string;
  readonly selectionOutline: string;
  readonly previewValid: string;
  readonly previewInvalid: string;
  readonly snapGuide: string;
  readonly marqueeFill: string;
  readonly marqueeStroke: string;
  readonly measureGuide: string;
  readonly measureLine: string;
  readonly measureLabelBg: string;
  readonly measureLabelFg: string;
  readonly measureHover: string;
  readonly hintStrong: string;
  readonly hintSoft: string;
  readonly ghostFill: string;
  readonly sketchWall: string;
  readonly sketchConstruction: string;
  readonly sketchVertex: string;
  /** Where a sketch point has caught, and on what. */
  readonly sketchSnap: string;
  /** What a press would take hold of. */
  readonly sketchHover: string;
  readonly shapeWall: string;
  readonly shapeWindow: string;
  readonly shapeDoor: string;
  readonly shapeFlint: string;
}

export const LIGHT_THEME: DrawTheme = {
  background: '#F4F1EA',
  gridAxis: '#A7B0B8',
  gridMajor: '#D2D5D5',
  gridMinor: '#E3E3DF',
  gridLabel: '#8B9298',
  gridCaption: '#7C858D',
  selectionOutline: '#1276A8',
  previewValid: '#23834A',
  previewInvalid: '#C1372A',
  snapGuide: '#167EA6',
  marqueeFill: 'rgba(134, 183, 213, 0.28)',
  marqueeStroke: '#1276A8',
  measureGuide: '#5E8A96',
  measureLine: '#C06416',
  measureLabelBg: '#263B46',
  measureLabelFg: '#FFFFFF',
  measureHover: '#F2A640',
  hintStrong: '#6C7781',
  hintSoft: '#899198',
  ghostFill: 'rgba(255,255,255,0.35)',
  sketchWall: '#1F4E6B',
  sketchConstruction: '#9AA7AE',
  sketchVertex: '#1F4E6B',
  // Not one of the drawing's own colours: a snap marker has to be legible
  // over brickwork, over a fill and over the grid, and be seen to be
  // temporary. The measure tool's amber is the one already doing that job.
  sketchSnap: '#E0761A',
  sketchHover: '#1276A8',
  shapeWall: 'rgba(31, 78, 107, 0.10)',
  shapeWindow: 'rgba(49, 122, 168, 0.20)',
  shapeDoor: 'rgba(168, 110, 32, 0.20)',
  shapeFlint: 'rgba(70, 76, 82, 0.26)',
};

export const DARK_THEME: DrawTheme = {
  background: '#171C21',
  gridAxis: '#5A666F',
  gridMajor: '#333C43',
  gridMinor: '#252C32',
  gridLabel: '#79858E',
  gridCaption: '#79858E',
  selectionOutline: '#4FADE0',
  previewValid: '#4FBE7E',
  previewInvalid: '#E4796A',
  snapGuide: '#54B0DA',
  marqueeFill: 'rgba(79, 173, 224, 0.22)',
  marqueeStroke: '#4FADE0',
  measureGuide: '#7FA9B4',
  measureLine: '#E39A50',
  measureLabelBg: '#0E1418',
  measureLabelFg: '#F2F6F8',
  measureHover: '#F2A640',
  hintStrong: '#8E9AA3',
  hintSoft: '#6B767E',
  ghostFill: 'rgba(0,0,0,0.30)',
  sketchWall: '#7FC2E4',
  sketchConstruction: '#69757D',
  sketchVertex: '#7FC2E4',
  sketchSnap: '#F2A640',
  sketchHover: '#4FADE0',
  shapeWall: 'rgba(127, 194, 228, 0.10)',
  shapeWindow: 'rgba(92, 172, 221, 0.22)',
  shapeDoor: 'rgba(222, 154, 78, 0.22)',
  shapeFlint: 'rgba(150, 160, 170, 0.26)',
};

const CACHE = new Map<string, string>();

function rgb(colour: string): [number, number, number] {
  const hex = colour.replace('#', '');
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ];
}

function hex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${clamp(r)}${clamp(g)}${clamp(b)}`.toUpperCase();
}

/** Move each channel a fraction toward white (clause 12.1). */
export function lighten(colour: string, amount: number): string {
  const key = `l${colour}${amount}`;
  const cached = CACHE.get(key);
  if (cached !== undefined) return cached;
  const [r, g, b] = rgb(colour);
  const result = hex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
  CACHE.set(key, result);
  return result;
}

/** Multiply each channel by (1 - amount) (clause 12.1). */
export function darken(colour: string, amount: number): string {
  const key = `d${colour}${amount}`;
  const cached = CACHE.get(key);
  if (cached !== undefined) return cached;
  const [r, g, b] = rgb(colour);
  const factor = 1 - amount;
  const result = hex(r * factor, g * factor, b * factor);
  CACHE.set(key, result);
  return result;
}

/** Black or white, whichever reads on the given ground (clause 12.1). */
export function contrastColour(colour: string): string {
  const [r, g, b] = rgb(colour);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.58 ? '#20252A' : '#FFFFFF';
}
